/**
 * Badge — small colored pill for tags, counts, statuses.
 */

import type { ReactNode } from 'react';

export type BadgeTone = 'neutral' | 'accent' | 'success' | 'warning' | 'danger';

export interface BadgeProps {
  tone?: BadgeTone;
  children?: ReactNode;
  /** Adds a dashed outline (used to mark "override" tags). */
  outlined?: boolean;
  title?: string;
  style?: React.CSSProperties;
}

const tones: Record<BadgeTone, { bg: string; fg: string }> = {
  neutral: { bg: 'var(--bg-tertiary)', fg: 'var(--text-secondary)' },
  accent: { bg: 'var(--accent-glow)', fg: 'var(--accent)' },
  success: { bg: 'var(--green-glow)', fg: 'var(--green)' },
  warning: { bg: 'var(--yellow-glow)', fg: 'var(--yellow)' },
  danger: { bg: 'var(--red-glow)', fg: 'var(--red)' },
};

export function Badge({ tone = 'neutral', outlined, children, title, style }: BadgeProps) {
  const t = tones[tone];
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 9px',
        fontSize: 11,
        fontWeight: 600,
        borderRadius: 999,
        background: t.bg,
        color: t.fg,
        outline: outlined ? `1px dashed ${t.fg}` : undefined,
        outlineOffset: outlined ? -2 : undefined,
        ...style,
      }}
    >
      {children}
    </span>
  );
}
